import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMemberAuth } from '../../contexts/MemberAuthContext';
import { useLang } from '../../contexts/LanguageContext';
import logo from '../../assets/styles/logo.png';
import LangSwitch from '../common/LangSwitch';
import {
  LayoutDashboard,
  CreditCard,
  ClipboardList,
  User,
  LogOut,
  Menu,
  X,
  Users
} from 'lucide-react';

const navItems = [
  { path: '/uye', labelTr: 'Dashboard', labelDe: 'Dashboard', icon: LayoutDashboard },
  { path: '/uye/aidatlar', labelTr: 'Aidatlarım', labelDe: 'Meine Beiträge', icon: CreditCard },
  { path: '/uye/giris-kayitlari', labelTr: 'Giriş Kayıtlarım', labelDe: 'Meine Einträge', icon: ClipboardList },
  { path: '/uye/profil', labelTr: 'Profilim', labelDe: 'Mein Profil', icon: User },
];

export default function MemberLayout({ children }) {
  const { member, logout } = useMemberAuth();
  const { lang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/uye') return location.pathname === '/uye';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <header className="bg-gs-red text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Title */}
            <Link to="/uye" className="flex items-center gap-2">
              <span className="font-bold text-lg">Galatasaray Gelsenkirchen e.V.</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-gs-yellow/20 text-gs-yellow'
                        : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Icon size={16} />
                    {lang === 'tr' ? item.labelTr : item.labelDe}
                  </Link>
                );
              })}
            </nav>

            {/* User info + logo + logout */}
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-sm text-white/80">
                {member?.first_name} {member?.last_name}
              </span>
              <LangSwitch />
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                title={lang === 'tr' ? 'Çıkış Yap' : 'Abmelden'}
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">{lang === 'tr' ? 'Çıkış' : 'Abmelden'}</span>
              </button>
              {/* Mobile menu toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-white/10 px-4 py-2 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-3 py-3 min-h-[44px] rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-gs-yellow/20 text-gs-yellow'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon size={16} />
                  {lang === 'tr' ? item.labelTr : item.labelDe}
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {children}
      </main>
    </div>
  );
}
